import { authService } from './auth';
import { resolveApiBase } from '../config/runtime';

const ENDPOINT = '/api/diagnostics/client-event';
const SEND_COOLDOWN_MS = 20_000;

const sendGuard = new Map<string, number>();

function canSend(key: string): boolean {
  const now = Date.now();
  const prev = sendGuard.get(key) ?? 0;
  if (now - prev < SEND_COOLDOWN_MS) return false;
  sendGuard.set(key, now);
  return true;
}

export async function reportClientDiagnostic(
  event: string,
  details: Record<string, unknown> = {},
  message = '',
): Promise<void> {
  const route = typeof window !== 'undefined' ? window.location.pathname + window.location.search : 'unknown';
  const key = `${event}:${route}`;
  if (!canSend(key)) return;

  const base = resolveApiBase();
  const token = authService.getToken();
  const payload = {
    event,
    route,
    message,
    timestamp: new Date().toISOString(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    details,
  };

  try {
    await fetch(`${base}${ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Best-effort diagnostics; never impact UI flow.
  }
}

export function installGlobalClientDiagnostics(): void {
  if (typeof window === 'undefined') return;

  let hoverWindowStart = Date.now();
  let hoverCount = 0;
  const hoverTargets = new Set<string>();

  const flushHoverWindow = () => {
    const now = Date.now();
    if (now - hoverWindowStart < 1500) return;
    if (hoverCount >= 35) {
      void reportClientDiagnostic('hover_storm_buttons', {
        eventsInWindow: hoverCount,
        windowMs: now - hoverWindowStart,
        uniqueTargets: Array.from(hoverTargets).slice(0, 12),
      }, 'High-frequency pointerover events on interactive controls');
    }
    hoverWindowStart = now;
    hoverCount = 0;
    hoverTargets.clear();
  };

  window.addEventListener('error', (event) => {
    void reportClientDiagnostic('window_error', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    }, event.message || 'window error');
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = (event.reason && (event.reason.message || String(event.reason))) || 'unhandled rejection';
    void reportClientDiagnostic('unhandled_rejection', {}, String(reason));
  });

  const vv = window.visualViewport;
  if (vv) {
    let lastHeight = vv.height;
    vv.addEventListener('resize', () => {
      const delta = Math.abs(vv.height - lastHeight);
      if (delta > 80) {
        void reportClientDiagnostic('viewport_resize_jump', {
          prevHeight: lastHeight,
          nextHeight: vv.height,
          offsetTop: vv.offsetTop,
          scale: vv.scale,
        }, 'visual viewport jump detected');
      }
      lastHeight = vv.height;
    });
  }

  window.addEventListener('pointerover', (event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const interactive = target.closest('button, [role="button"], a, .btn, .topnav__icon-btn, .sidebar__nav-item, .sidebar__action-btn');
    if (!interactive) {
      flushHoverWindow();
      return;
    }

    hoverCount += 1;
    const tag = (interactive as HTMLElement).tagName.toLowerCase();
    const className = ((interactive as HTMLElement).className || '').toString().split(' ').filter(Boolean).slice(0, 2).join('.');
    hoverTargets.add(className ? `${tag}.${className}` : tag);
    flushHoverWindow();
  }, { passive: true, capture: true });

  _observeInitialLoad();
  _observeLcp();
  _observeSpaNavigation();
  _observeLongTasks();
}

// ---------------------------------------------------------------------------
// Performance observers (called once from installGlobalClientDiagnostics)
// ---------------------------------------------------------------------------

/** Report if initial page load metrics exceed thresholds. */
function _observeInitialLoad(): void {
  const report = () => {
    const entries = performance.getEntriesByType('navigation');
    if (!entries.length) return;
    const nav = entries[0] as PerformanceNavigationTiming;
    const ttfb = Math.round(nav.responseStart - nav.requestStart);
    const dcl = Math.round(nav.domContentLoadedEventEnd - nav.startTime);
    const load = Math.round(nav.loadEventEnd - nav.startTime);
    if (ttfb > 800 || dcl > 3000 || load > 5000) {
      void reportClientDiagnostic(
        'perf_initial_load_slow',
        { ttfb, dcl, load },
        `Slow initial load — ttfb:${ttfb}ms dcl:${dcl}ms load:${load}ms`,
      );
    }
  };
  // Schedule after current microtask queue so Navigation Timing entries are flushed.
  if (document.readyState === 'complete') {
    window.setTimeout(report, 0);
  } else {
    window.addEventListener('load', () => window.setTimeout(report, 0), { once: true });
  }
}

/** Observe Largest Contentful Paint and report if poor (>2.5s) or needs improvement (>1.2s). */
function _observeLcp(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  let lcpMs = 0;
  let done = false;
  const flush = () => {
    if (done) return;
    done = true;
    if (lcpMs > 2500) {
      void reportClientDiagnostic(
        'perf_lcp_poor',
        { lcp: Math.round(lcpMs) },
        `LCP ${Math.round(lcpMs)}ms exceeds 2.5s threshold`,
      );
    } else if (lcpMs > 1200) {
      void reportClientDiagnostic(
        'perf_lcp_needs_improvement',
        { lcp: Math.round(lcpMs) },
        `LCP ${Math.round(lcpMs)}ms needs improvement`,
      );
    }
  };
  try {
    const po = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      if (entries.length) lcpMs = entries[entries.length - 1].startTime;
    });
    po.observe({ type: 'largest-contentful-paint', buffered: true });
    // LCP is finalised on first user interaction or tab hide.
    ['keydown', 'click', 'pointerdown', 'touchstart'].forEach((evt) =>
      window.addEventListener(evt, flush, { once: true, passive: true }),
    );
    document.addEventListener('visibilitychange', () => { if (document.hidden) flush(); }, { once: true });
  } catch {
    // LCP PerformanceObserver not supported in this environment.
  }
}

/** Patch history.pushState/replaceState to measure SPA route render time. */
function _observeSpaNavigation(): void {
  let t0 = 0;
  let fromPath = '';

  const onStart = () => {
    t0 = performance.now();
    fromPath = window.location.pathname;
  };

  const onEnd = () => {
    if (!t0) return;
    const elapsed = Math.round(performance.now() - t0);
    const toPath = window.location.pathname;
    t0 = 0;
    if (elapsed > 400) {
      void reportClientDiagnostic(
        'perf_spa_nav_slow',
        { from: fromPath, to: toPath, elapsedMs: elapsed },
        `Slow SPA navigation ${fromPath} → ${toPath}: ${elapsed}ms`,
      );
    }
  };

  type PushStateFn = typeof history.pushState;
  const wrapNav = (orig: PushStateFn): PushStateFn =>
    function (this: History, data: unknown, unused: string, url?: string | URL | null): void {
      onStart();
      orig.call(this, data, unused, url);
      requestAnimationFrame(() => requestAnimationFrame(onEnd));
    };

  history.pushState = wrapNav(history.pushState);
  history.replaceState = wrapNav(history.replaceState);
  window.addEventListener('popstate', () => {
    onStart();
    requestAnimationFrame(() => requestAnimationFrame(onEnd));
  });
}

/** Report main-thread tasks that block for longer than 300ms. */
function _observeLongTasks(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 300) {
          void reportClientDiagnostic(
            'perf_long_task',
            { duration: Math.round(entry.duration), startTime: Math.round(entry.startTime) },
            `Long task ${Math.round(entry.duration)}ms on ${window.location.pathname}`,
          );
        }
      }
    });
    po.observe({ type: 'longtask', buffered: false });
  } catch {
    // longtask PerformanceObserver not supported (e.g. Firefox).
  }
}
